import { useParams, Link } from 'react-router-dom';
import { articles, categories } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import ScrollToTop from '@/components/feature/ScrollToTop';

const articleHeroImages: Record<string, string> = {
  'asmr-beginner-guide': 'https://readdy.ai/api/search-image?query=Soft%20warm%20cozy%20bedroom%20with%20headphones%20on%20a%20wooden%20nightstand%2C%20gentle%20morning%20sunlight%20through%20sheer%20curtains%2C%20cream%20and%20beige%20tones%2C%20peaceful%20atmosphere%2C%20editorial%20photography%2C%20no%20text&width=1800&height=700&seq=art-hero-guide&orientation=landscape',
  'top-asmr-headphones-2026': 'https://readdy.ai/api/search-image?query=Luxurious%20headphone%20collection%20on%20a%20minimalist%20wooden%20display%2C%20soft%20warm%20studio%20lighting%2C%20cream%20background%2C%20premium%20product%20photography%2C%20elegant%20composition%2C%20no%20text&width=1800&height=700&seq=art-hero-headphones&orientation=landscape',
  'rain-soundscape-guide': 'https://readdy.ai/api/search-image?query=Raindrops%20on%20a%20window%20with%20soft%20blurred%20green%20garden%2C%20cozy%20indoor%20atmosphere%2C%20warm%20lighting%2C%20peaceful%20rainy%20day%20mood%2C%20cream%20and%20muted%20green%20tones%2C%20fine%20art%20photography%2C%20no%20text&width=1800&height=700&seq=art-hero-rain&orientation=landscape',
  'trigger-sensitivity-training': 'https://readdy.ai/api/search-image?query=Person%20meditating%20with%20headphones%20in%20a%20minimalist%20sunlit%20room%2C%20serene%20atmosphere%2C%20cream%20walls%2C%20wooden%20floor%2C%20warm%20natural%20light%2C%20editorial%20photography%2C%20no%20text&width=1800&height=700&seq=art-hero-training&orientation=landscape',
  'white-noise-sleep-guide': 'https://readdy.ai/api/search-image?query=Peaceful%20bedroom%20at%20night%20with%20ambient%20lighting%2C%20white%20linen%20bed%2C%20small%20speaker%2C%20cozy%20warm%20atmosphere%2C%20minimalist%20interior%2C%20cream%20tones%2C%20tranquil%20mood%2C%20no%20text&width=1800&height=700&seq=art-hero-sleep&orientation=landscape',
  'binaural-recording-secrets': 'https://readdy.ai/api/search-image?query=Professional%20microphone%20in%20a%20minimalist%20home%20studio%20with%20acoustic%20panels%2C%20soft%20warm%20lighting%2C%20cream%20walls%2C%20clean%20wooden%20desk%2C%20editorial%20photography%2C%20no%20text&width=1800&height=700&seq=art-hero-binaural&orientation=landscape',
  'asmr-youtubers-top10': 'https://readdy.ai/api/search-image?query=Cozy%20content%20creator%20desk%20with%20microphone%20and%20headphones%2C%20warm%20ring%20light%2C%20acoustic%20panels%2C%20cream%20and%20beige%20tones%2C%20studio%20aesthetic%2C%20no%20text&width=1800&height=700&seq=art-hero-youtubers&orientation=landscape',
  'open-back-vs-closed-back': 'https://readdy.ai/api/search-image?query=Two%20elegant%20headphone%20pairs%20on%20a%20minimalist%20surface%2C%20soft%20natural%20light%2C%20cream%20background%2C%20premium%20product%20photography%2C%20clean%20composition%2C%20no%20text&width=1800&height=700&seq=art-hero-openclosed&orientation=landscape',
  'nature-soundscape-top5': 'https://readdy.ai/api/search-image?query=Beautiful%20misty%20forest%20with%20morning%20sunlight%20rays%2C%20peaceful%20serene%20nature%2C%20warm%20cream%20and%20sage%20tones%2C%20dreamy%20ethereal%20atmosphere%2C%20fine%20art%20photography%2C%20no%20text&width=1800&height=700&seq=art-hero-nature&orientation=landscape',
  'asmr-for-anxiety': 'https://readdy.ai/api/search-image?query=Peaceful%20minimalist%20corner%20with%20comfortable%20chair%20and%20headphones%2C%20warm%20sunlight%2C%20indoor%20plants%2C%20cream%20walls%2C%20calming%20atmosphere%2C%20editorial%20interior%20photography%2C%20no%20text&width=1800&height=700&seq=art-hero-anxiety&orientation=landscape',
  'asmr-microphone-guide': 'https://readdy.ai/api/search-image?query=Professional%20microphones%20on%20a%20wooden%20desk%2C%20soft%20studio%20lighting%2C%20cream%20background%2C%20product%20photography%2C%20clean%20elegant%20composition%2C%20no%20text&width=1800&height=700&seq=art-hero-mic&orientation=landscape',
  'midnight-asmr-playlist': 'https://readdy.ai/api/search-image?query=Cozy%20bedroom%20at%20night%20with%20fairy%20lights%2C%20headphones%20on%20soft%20blankets%2C%20warm%20amber%20tones%2C%20dreamy%20intimate%20mood%2C%20editorial%20style%2C%20no%20text&width=1800&height=700&seq=art-hero-playlist&orientation=landscape',
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const category = article
    ? categories.find((c) => c.slug === article.category)
    : undefined;

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h1>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const heroImage = articleHeroImages[article.slug] || articleHeroImages['asmr-beginner-guide'];
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '耳骚',
    },
    url: `https://ersao.fun/article/${article.slug}`,
    inLanguage: 'zh-CN',
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navbar />
      <main>
        <article>
          <section className="relative w-full h-[300px] md:h-[420px] flex items-end overflow-hidden">
            <img
              src={heroImage}
              alt={article.title}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/35 to-black/20"></div>
            <div className="relative z-10 w-full px-4 md:px-6 pb-8 md:pb-12">
              <div className="max-w-3xl mx-auto">
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="inline-block px-3 py-1 rounded-full bg-background-50/20 text-background-50 text-xs font-medium mb-3 hover:bg-background-50/30 transition-colors whitespace-nowrap"
                  >
                    {category.name}
                  </Link>
                )}
                <h1 className="text-xl md:text-3xl lg:text-4xl font-bold font-heading text-background-50 leading-snug mb-3">
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 text-xs text-background-200/80 flex-wrap">
                  <span>{article.author}</span>
                  <span>·</span>
                  <span>{article.date}</span>
                  <span>·</span>
                  <span>{article.readTime}</span>
                </div>
              </div>
            </div>
          </section>

          <section className="py-8 md:py-12 px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <div className="mb-6">
                <Breadcrumb
                  items={[
                    { label: '首页', path: '/' },
                    ...(category ? [{ label: category.name, path: `/category/${category.slug}` }] : []),
                    { label: article.title },
                  ]}
                />
              </div>

              <div className="flex items-center gap-2 mb-8 flex-wrap">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
                  [&_h3]:text-xl [&_h3]:md:text-2xl [&_h3]:font-heading [&_h3]:font-bold [&_h3]:text-foreground-900 [&_h3]:mt-8 [&_h3]:mb-4
                  [&_p]:mb-4 [&_p]:leading-relaxed
                  [&_strong]:text-foreground-900 [&_strong]:font-semibold"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-12 pt-8 border-t border-background-200">
                <p className="text-sm text-foreground-500">
                  作者：{article.author} · 发布于 {article.date}
                </p>
              </div>
            </div>
          </section>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-16 px-4 md:px-6 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-8 text-center">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => {
                  const raCat = categories.find((c) => c.slug === ra.category);
                  return <ArticleCard key={ra.slug} article={ra} category={raCat} />;
                })}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}